import { useConfigurations } from "@via/api/configuration.tsx";
import EntityTagsSection from "@via/components/entity-tags-section.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import UserGroupSelectComponent from "@via/components/select/user-group-select-component";
import { useAbility } from "@via/context/ability-context.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useGlobal } from "@via/hooks/useGlobal";
import ConfirmPermissionChangeModal from "@via/pages/projects/confirm-permission-change-modal.tsx";
import CreateMetadataTrackerModal from "@via/pages/projects/create-metadata-tracker-modal.tsx";
import { useCreateProjectBucket } from "@via/pages/projects/project-bucket.api.tsx";
import {
  useProject,
  useUpdateProject,
  useVmetaProjects,
} from "@via/pages/projects/projects.api.tsx";
import { getPermissionsEncoding } from "@via/pages/projects/runs/utils/helpers.tsx";
import ProjectBucketMemberModal from "@via/pages/projects/settings/project-bucket-member-modal.tsx";
import { ROUTES, USER_ROLES } from "@via/utils/constants.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import {
  Permissions,
  PermissionType,
  TaggableEntityType,
  VmetaProject,
} from "common";
import { type JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useDebounceCallback } from "usehooks-ts";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

const defaultParams = {
  sort: [],
  page: 0,
  pageSize: 10,
};

const noneOption = { label: "None", value: null };

export default function ProjectSettings(): JSX.Element {
  const { user } = useAuth();
  const { projectId = "" } = useParams();

  const [page, setPage] = useState(defaultParams.page);
  const pageSize = defaultParams.pageSize;

  const [search, setSearch] = useState("");

  const [cumulativeVmetaProjects, setCumulativeVmetaProjects] = useState<
    VmetaProject[]
  >([]);

  const [missingVmetaProjects, setMissingVmetaProjects] = useState<
    VmetaProject[]
  >([]);

  const { data: projectData, isSuccess: isProjectSuccess } =
    useProject(projectId);
  const { mutate: updateProject, isPending: isUpdateProjectLoading } =
    useUpdateProject();
  const { setSelectedProject } = useGlobal();

  const [selectedVmetaProject, setSelectedVmetaProject] =
    useState<SelectOption | null>(noneOption);
  const [
    isCreateMetadataTrackerModalOpen,
    setIsCreateMetadataTrackerModalOpen,
  ] = useState(false);
  const [isProjectBucketMemberModalOpen, setIsProjectBucketMemberModalOpen] =
    useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<{
    prevType: PermissionType;
    prevGroupId?: number | null;
    prevGroupLabel?: string | null;

    nextType: PermissionType;
    nextValue: Permissions;
    nextGroupId?: number | null;
    nextGroupLabel?: string | null;
  } | null>(null);

  const currentPermissionType = useMemo(() => {
    // your existing helper already does this elsewhere:
    // getPermissionsEncoding(projectData?.permission as Permissions)
    return (
      getPermissionsEncoding(projectData?.permission as Permissions) ??
      PermissionType.UserOwned
    );
  }, [projectData?.permission]);

  const currentGroupId = projectData?.groupId ?? null;
  // If you have the current group label handy, pass it; otherwise null is fine.
  const currentGroupLabel: string | null = null;

  const {
    data: vmetaProjects,
    isPending: isVmetaProjectsLoading,
    isError: isVmetaProjectsError,
  } = useVmetaProjects(page, pageSize, defaultParams.sort, [
    {
      id: "name",
      value: search,
    },
  ]);

  // Fetch missing selected vmeta project separately
  const {
    data: vmetaMissingProjects,
    isLoading: isVmetaMissingProjectsLoading,
  } = useVmetaProjects(
    0,
    10,
    [],
    [{ id: "_id", value: projectData?.vmetaId }],
    projectData?.vmetaId ? true : false // only fetch if there is a vmetaId
  );

  const {
    mutate: createProjectBucket,
    isPending: isCreateProjectBucketLoading,
  } = useCreateProjectBucket();

  const { data: configurations } = useConfigurations();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const defaultPermission =
    isAdmin && configurations?.ENABLE_SHARE_WITH_EVERYONE_PROJECT
      ? PermissionType.Public
      : PermissionType.UserOwned;
  const [permissions, setPermissions] =
    useState<PermissionType>(defaultPermission);
  const [selectedWriteGroupIds, setSelectedWriteGroupIds] = useState<number[]>(
    projectData?.writeGroupIds ?? []
  );

  const ability = useAbility([
    {
      action: "View",
      subject: "Button",
      condition: projectData?.own,
    },
    {
      action: "Edit",
      subject: "Input",
      condition: projectData?.own,
    },
    {
      action: "Submit",
      subject: "Button",
      condition: projectData?.own,
    },
  ]);

  useEffect(() => {
    if (vmetaMissingProjects?.data) {
      setMissingVmetaProjects(vmetaMissingProjects.data);
    }
  }, [vmetaMissingProjects]);

  useEffect(() => {
    if (isProjectSuccess) {
      const perms = getPermissionsEncoding(
        projectData?.permission as Permissions
      );
      setPermissions(perms ?? defaultPermission);

      if (projectData) {
        setSelectedProject(projectData);
        setSelectedWriteGroupIds(projectData.writeGroupIds ?? []);
      }
    }
  }, [projectData, isProjectSuccess]);

  // Only an admin may share a project publicly.
  const isPublicOptionDisabled = !isAdmin;

  const handleGroupIdUpdate = selected => {
    // When user picks a new group, stage the change based on *server* state
    setPendingChange({
      prevType: currentPermissionType,
      prevGroupId: currentGroupId,
      prevGroupLabel: currentGroupLabel,

      nextType: PermissionType.GroupShared,
      nextValue: Permissions.GroupShared,
      nextGroupId: selected.value,
      nextGroupLabel: selected?.label ?? null,
    });
    setConfirmModalOpen(true);
  };

  const handlePermissionUpdate = (type: PermissionType, value: Permissions) => {
    setPendingChange({
      prevType: currentPermissionType,
      prevGroupId: currentGroupId,
      prevGroupLabel: currentGroupLabel,

      nextType: type,
      nextValue: value,
      nextGroupId: null,
      nextGroupLabel: null,
    });
    setConfirmModalOpen(true);
  };

  const confirmPermissionChange = () => {
    if (!pendingChange) return;

    const { nextType, nextValue, nextGroupId } = pendingChange;

    if (nextType !== PermissionType.GroupShared) {
      updateProject({
        id: projectId,
        data: { permission: nextValue, groupId: null },
      });
    } else {
      updateProject({
        id: projectId,
        data: {
          permission: Permissions.GroupShared,
          groupId: nextGroupId ?? null,
        },
      });
    }

    // Reflect new UI state
    setPermissions(nextType);
    setConfirmModalOpen(false);
    setPendingChange(null);
  };

  const cancelPermissionChange = () => {
    if (pendingChange) {
      setPermissions(pendingChange.prevType);
    }
    setPendingChange(null);
    setConfirmModalOpen(false);
  };

  const handleVmetaProjectChange = selected => {
    setSelectedVmetaProject(selected);
  };

  const handleSaveVmetaProjectClick = () => {
    if (selectedVmetaProject && selectedVmetaProject.value !== undefined) {
      updateProject({
        id: projectId,
        data: {
          vmetaId: selectedVmetaProject.value,
        },
      });
    }
  };

  const handleCreateProjectBucket = () => {
    createProjectBucket({
      projectId: +projectId,
    });
  };

  const handleSaveWriteGroups = () => {
    updateProject({
      id: projectId,
      data: { writeGroupIds: selectedWriteGroupIds },
    });
  };

  useEffect(() => {
    if (vmetaProjects?.data) {
      setCumulativeVmetaProjects(prev => [
        ...prev,
        ...vmetaProjects.data.filter(
          project => !prev.some(p => p._id === project._id)
        ),
      ]);
    }
  }, [vmetaProjects?.data, search, page]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === search) return;
    setPage(0);
    setSearch(value);
  }, 300);

  const vmetaProjectOptions = useMemo(() => {
    const map = new Map();
    cumulativeVmetaProjects.forEach(project => {
      map.set(project._id, project);
    });
    missingVmetaProjects.forEach(project => {
      map.set(project._id, project);
    });
    const mergedVmetaProjects: VmetaProject[] = Array.from(map.values());

    return [
      noneOption,
      ...(mergedVmetaProjects.map(project => ({
        label: project.label,
        value: project._id,
      })) ?? []),
    ];
  }, [cumulativeVmetaProjects, missingVmetaProjects]);

  useEffect(() => {
    if (missingVmetaProjects.length) {
      setSelectedVmetaProject({
        label: missingVmetaProjects[0].label,
        value: missingVmetaProjects[0]._id,
      });
    }
  }, [missingVmetaProjects]);

  const fetchMoreVmetaProjects = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((vmetaProjects?.results ?? 0) / pageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [vmetaProjects?.results]);

  const getProjectSettingsContext = useCallback(() => {
    return {
      project: {
        id: projectData?.id,
        name: projectData?.name,
        own: projectData?.own,
        owner: projectData?.owner,
      },
      settings: {
        permissions: {
          type: permissions,
          permission: projectData?.permission,
          groupId: projectData?.groupId,
        },
        metadataTracker: selectedVmetaProject
          ? {
              id: selectedVmetaProject.value,
              name: selectedVmetaProject.label,
            }
          : null,
        projectBucket: projectData?.projectBucket || null,
        availableMetadataTrackers: vmetaProjectOptions
          .filter(option => option.value !== null)
          .map(option => ({
            id: option.value,
            name: option.label,
          })),
      },
      user: {
        role: user?.userRole,
        canEdit: ability.can("Edit", "Input"),
        canSubmit: ability.can("Submit", "Button"),
      },
    };
  }, [
    projectData,
    permissions,
    selectedVmetaProject,
    vmetaProjectOptions,
    user?.userRole,
    ability,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.PROJECT_SETTINGS,
    contextName: "Project Settings",
    getContext: getProjectSettingsContext,
    systemPrompt:
      "You are analyzing project settings page. Help answer questions about project settings, permissions, metadata tracker, and project bucket configuration.",
    enabled: !!projectData && isProjectSuccess,
  });

  return (
    <div className="flex h-[74dvh] flex-row justify-start">
      <fieldset className="w-full pr-8">
        {isVmetaProjectsError ? null : (
          <>
            <div className="pt-3 gap-4 flex items-end">
              <div className="flex-2 min-w-80">
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Metadata Tracker
                </label>
                <div>
                  <SelectComponent
                    name="userId"
                    options={vmetaProjectOptions}
                    value={selectedVmetaProject}
                    onChange={handleVmetaProjectChange}
                    isLoading={
                      isVmetaProjectsLoading || isVmetaMissingProjectsLoading
                    }
                    placeholder={"Choose Metadata Tracker"}
                    enableClear={false}
                    onFetchMoreOptions={fetchMoreVmetaProjects}
                    onInputChange={inputValue =>
                      debouncedHandleInputChange(inputValue)
                    }
                  />
                </div>
              </div>
              <div>
                <button
                  className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={handleSaveVmetaProjectClick}
                  disabled={
                    isUpdateProjectLoading || !ability.can("Edit", "Input")
                  }>
                  <Spinner loading={isUpdateProjectLoading}>Save</Spinner>
                </button>
              </div>
              <div>
                <button
                  className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => setIsCreateMetadataTrackerModalOpen(true)}
                  disabled={
                    isUpdateProjectLoading || !ability.can("Edit", "Input")
                  }>
                  <Spinner loading={isUpdateProjectLoading}>
                    Create Metadata Tracker
                  </Spinner>
                </button>
              </div>
            </div>
            <hr className="mt-6 border-gray-200" />
          </>
        )}
        {user?.allowedActions?.allowProjectBucket ? (
          <>
            <div className="pt-3 gap-4 flex items-end">
              <div className="flex-2 min-w-80">
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Project Bucket
                </label>
                <div>
                  <input
                    type="text"
                    className="w-full block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 disabled:opacity-50 disabled:text-gray-900 disabled:bg-gray-300"
                    disabled={true}
                    value={projectData?.projectBucket || ""}
                  />
                </div>
              </div>
              {projectData?.projectBucket ? (
                <div>
                  <button
                    className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => setIsProjectBucketMemberModalOpen(true)}>
                    View Bucket Permission
                  </button>
                </div>
              ) : (
                <div>
                  <button
                    className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={handleCreateProjectBucket}
                    disabled={
                      isUpdateProjectLoading || isCreateProjectBucketLoading
                    }>
                    Create Project Bucket
                  </button>
                </div>
              )}
            </div>
            <hr className="mt-6 border-gray-200" />
          </>
        ) : null}

        <label className="block pt-3 text-sm font-medium leading-6 text-gray-900">
          Set Permissions
        </label>
        <div className="mt-2 space-y-4">
          <div
            className={classNames(
              "relative flex items-start",
              isPublicOptionDisabled ? "opacity-50" : ""
            )}>
            <div className="absolute flex h-6 items-center">
              <input
                id="permission-public"
                disabled={isPublicOptionDisabled}
                checked={permissions === PermissionType.Public}
                onChange={() =>
                  handlePermissionUpdate(
                    PermissionType.Public,
                    Permissions.Public
                  )
                }
                value={PermissionType.Public}
                aria-describedby="permission-public-description"
                type="radio"
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
            </div>
            <div className="pl-7 text-sm leading-6">
              <label
                htmlFor="permission-public"
                className="font-medium text-gray-900">
                Public access
              </label>
              <p id="permission-public-description" className="text-gray-500">
                Everyone with the link will see this project.
                {!isAdmin &&
                  " Only administrators can share projects publicly."}
              </p>
            </div>
          </div>

          <div>
            <div
              className={classNames(
                "relative flex items-start",
                isPublicOptionDisabled && !ability.can("Edit", "Input")
                  ? "opacity-50"
                  : ""
              )}>
              <div className="absolute flex h-6 items-center">
                <input
                  id="permission-group"
                  disabled={!ability.can("Edit", "Input")}
                  checked={permissions === PermissionType.GroupShared}
                  onChange={() => setPermissions(PermissionType.GroupShared)}
                  value={PermissionType.GroupShared}
                  aria-describedby="permission-group-description"
                  type="radio"
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </div>
              <div className="pl-7 text-sm leading-6">
                <label
                  htmlFor="permission-group"
                  className="font-medium text-gray-900">
                  Share with a group
                </label>
                <p id="permission-group-description" className="text-gray-500">
                  Only users in the selected group would be able to access.
                </p>
              </div>
            </div>
          </div>
          {permissions === PermissionType.GroupShared && (
            <UserGroupSelectComponent
              name="groupId"
              isDisabled={!ability.can("Edit", "Input")}
              menuPlacement="top"
              {...(projectData?.groupId
                ? { missingId: [projectData.groupId] }
                : [])}
              onChange={handleGroupIdUpdate}
            />
          )}
          {configurations?.ENABLE_SHARE_BUTTON && (
            <div>
              <div
                className={classNames(
                  "relative flex items-start",
                  isPublicOptionDisabled && !ability.can("Edit", "Input")
                    ? "opacity-50"
                    : ""
                )}>
                <div className="absolute flex h-6 items-center">
                  <input
                    id="permission-specific-users"
                    disabled={true}
                    checked={permissions === PermissionType.SpecificUsersShared}
                    value={PermissionType.SpecificUsersShared}
                    onChange={() =>
                      handlePermissionUpdate(
                        PermissionType.SpecificUsersShared,
                        Permissions.SpecificUsersShared
                      )
                    }
                    aria-describedby="permission-specific-users-description"
                    type="radio"
                    className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                </div>
                <div className="pl-7 text-sm leading-6">
                  <label
                    htmlFor="permission-specific-users"
                    className="font-medium text-gray-900">
                    Share with specific users
                  </label>
                  <p
                    id="permission-specific-users-description"
                    className="text-gray-500">
                    Only users you share this project with would be able to
                    access.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <div
              className={classNames(
                "relative flex items-start",
                isPublicOptionDisabled && !ability.can("Edit", "Input")
                  ? "opacity-50"
                  : ""
              )}>
              <div className="absolute flex h-6 items-center">
                <input
                  id="permission-private"
                  disabled={!ability.can("Edit", "Input")}
                  checked={permissions === PermissionType.UserOwned}
                  value={PermissionType.UserOwned}
                  onChange={() =>
                    handlePermissionUpdate(
                      PermissionType.UserOwned,
                      Permissions.UserOwned
                    )
                  }
                  aria-describedby="permission-private-description"
                  type="radio"
                  className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                />
              </div>
              <div className="pl-7 text-sm leading-6">
                <label
                  htmlFor="permission-private"
                  className="font-medium text-gray-900">
                  Private to you
                </label>
                <p
                  id="permission-private-description"
                  className="text-gray-500">
                  You are the only one able to access this project.
                </p>
              </div>
            </div>
          </div>
        </div>
        <hr className="mt-6 border-gray-200" />
        <div className="pt-3">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            Group Permission to Write
          </label>
          <p className="text-sm text-gray-500">
            Members of these groups can edit and delete this project and its
            runs, independent of the access permission above.
          </p>
          <div className="mt-2 flex items-end gap-4">
            <div className="flex-2 min-w-80">
              <UserGroupSelectComponent
                name="writeGroupId"
                isMulti={true}
                isDisabled={!ability.can("Edit", "Input")}
                {...(selectedWriteGroupIds.length
                  ? { missingId: selectedWriteGroupIds }
                  : {})}
                onChange={(value: unknown) => {
                  const options = (Array.isArray(value) ? value : []) as {
                    value: number;
                  }[];
                  setSelectedWriteGroupIds(
                    options.map(option => Number(option.value))
                  );
                }}
              />
            </div>
            <div>
              <button
                className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                onClick={handleSaveWriteGroups}
                disabled={
                  isUpdateProjectLoading || !ability.can("Edit", "Input")
                }>
                <Spinner loading={isUpdateProjectLoading}>Save</Spinner>
              </button>
            </div>
          </div>
        </div>
        <EntityTagsSection
          entityType={TaggableEntityType.Project}
          entityId={projectId}
          canEdit={ability.can("Edit", "Input")}
        />
      </fieldset>
      {isCreateMetadataTrackerModalOpen && (
        <CreateMetadataTrackerModal
          open={isCreateMetadataTrackerModalOpen}
          setOpen={setIsCreateMetadataTrackerModalOpen}
          projectId={projectId}
          updateProject={updateProject}
        />
      )}
      {isProjectBucketMemberModalOpen && (
        <ProjectBucketMemberModal
          open={isProjectBucketMemberModalOpen}
          setOpen={setIsProjectBucketMemberModalOpen}
          projectId={+projectId}
        />
      )}
      {confirmModalOpen && pendingChange && (
        <ConfirmPermissionChangeModal
          open={confirmModalOpen}
          setOpen={setConfirmModalOpen}
          currentType={pendingChange.prevType}
          currentGroupId={pendingChange.prevGroupId}
          currentGroupLabel={pendingChange.prevGroupLabel}
          nextType={pendingChange.nextType}
          nextGroupLabel={pendingChange.nextGroupLabel}
          nextGroupId={pendingChange.nextGroupId}
          isConfirmLoading={isUpdateProjectLoading}
          onConfirm={confirmPermissionChange}
          onCancel={cancelPermissionChange}
          resource="project"
        />
      )}
    </div>
  );
}
